import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { getCmsCollection } from '../lib/cms'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [navItems, setNavItems] = useState([])
  const location = useLocation()

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Fetch navigation items from CMS and filter out removed items
  useEffect(() => {
    let isMounted = true
    getCmsCollection('navigation_items').then((items) => {
      if (isMounted && items && items.length > 0) {
        const filtered = items
          .filter((item) => item.url !== '/hifi' && item.url !== '/referencer')
          .sort((a, b) => (a.order || 0) - (b.order || 0))
        setNavItems(filtered)
      }
    })
    return () => {
      isMounted = false
    }
  }, [])

  const defaultNav = [
    { url: '/', label: 'Hjem' },
    { url: '/om-mig', label: 'Om mig' },
    { url: '/ydelser', label: 'Ydelser' },
    { url: '/kontakt', label: 'Kontakt', is_highlighted: true },
  ]

  const displayNav =
    navItems.length > 0
      ? navItems.filter((item) => item.url !== '/hifi' && item.url !== '/referencer')
      : defaultNav

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-[#121214]/95 backdrop-blur-md transition-colors">
      {/* Top micro bar with contact info */}
      <div className="border-b border-white/5 bg-[#0E0E10] text-xs text-[#A8A49C]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#C87D46]" />
            <span className="italic font-serif text-[#E5E2DB]">“Musik med nerve”</span>
            <span className="hidden sm:inline text-white/20">|</span>
            <span className="hidden sm:inline text-[#A8A49C]">Lytterum & Værksted på Sydals</span>
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            <a
              href="tel:+4522884536"
              className="flex items-center gap-1.5 text-[#E5E2DB] hover:text-[#C87D46] transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[10px] text-[#C87D46]" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium">+45 22 88 45 36</span>
            </a>
            <a
              href="mailto:jens@jarcon.dk"
              className="hidden sm:flex items-center gap-1.5 text-[#E5E2DB] hover:text-[#C87D46] transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[10px] text-[#C87D46]" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium">jens@jarcon.dk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation container */}
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Brand logo & tagline (text brand container) */}
        <Link to="/" className="flex items-center group">
          <div className="flex flex-col">
            <span className="font-serif text-lg sm:text-xl tracking-wide text-[#F5F3EF] group-hover:text-[#C87D46] transition-colors font-medium leading-tight">
              JARC Audio
            </span>
            <span className="text-[11px] sm:text-xs uppercase tracking-widest text-[#A8A49C] mt-0.5">
              HiFi & Lyd
            </span>
          </div>
        </Link>

        {/* Desktop Navigation (EXACTLY 1200px and up) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1 xl:gap-2">
          {displayNav.map((item) => {
            const isContact = item.is_highlighted || item.url === '/kontakt'

            if (isContact) {
              return (
                <NavLink
                  key={item.url}
                  to={item.url}
                  className={({ isActive }) =>
                    `ml-3 px-5 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                      isActive
                        ? 'bg-[#C87D46] text-white shadow-lg shadow-[#C87D46]/20'
                        : 'bg-[#1E1E24] text-[#F5F3EF] border border-[#C87D46]/40 hover:bg-[#C87D46] hover:text-white'
                    }`
                  }
                >
                  <i className="fa-solid fa-mug-hot text-xs" aria-hidden="true" />
                  <span>{item.label}</span>
                </NavLink>
              )
            }

            return (
              <NavLink
                key={item.url}
                to={item.url}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-sm text-sm font-medium transition-colors duration-150 relative ${
                    isActive
                      ? 'text-[#C87D46] font-semibold'
                      : 'text-[#E5E2DB] hover:text-[#C87D46]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{item.label}</span>
                    {isActive && (
                      <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-[#C87D46] rounded-full" />
                    )}
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* Mobile / Tablet Burger Button (shown strictly UNDER 1200px) */}
        <div className="flex items-center gap-3 min-[1200px]:hidden">
          <Link
            to="/kontakt"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-sm bg-[#1E1E24] px-3.5 py-1.5 text-xs font-medium text-[#F5F3EF] border border-[#C87D46]/40 hover:bg-[#C87D46] hover:text-white transition-colors"
          >
            <i className="fa-solid fa-mug-hot text-[10px]" aria-hidden="true" />
            <span className="whitespace-nowrap">Kom forbi</span>
          </Link>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="inline-flex items-center justify-center p-2.5 rounded-sm bg-[#18181C] border border-white/10 text-[#F5F3EF] hover:text-[#C87D46] hover:border-[#C87D46]/40 transition-colors"
            aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
            aria-expanded={mobileMenuOpen}
          >
            <i
              className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Mobile / Tablet Navigation Drawer (strictly under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-white/10 bg-[#141418] px-4 pt-3 pb-6 shadow-2xl animate-in slide-in-from-top duration-200">
          <nav className="flex flex-col space-y-1">
            {displayNav.map((item) => {
              const isContact = item.is_highlighted || item.url === '/kontakt'

              if (isContact) {
                return (
                  <NavLink
                    key={item.url}
                    to={item.url}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `mt-3 flex items-center justify-center gap-2 rounded-sm px-4 py-3 text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-[#C87D46] text-white'
                          : 'bg-[#C87D46]/20 text-[#D98E58] border border-[#C87D46]/40 hover:bg-[#C87D46] hover:text-white'
                      }`
                    }
                  >
                    <i className="fa-solid fa-mug-hot text-xs" aria-hidden="true" />
                    <span>{item.label}</span>
                  </NavLink>
                )
              }

              return (
                <NavLink
                  key={item.url}
                  to={item.url}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-sm px-3.5 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-white/10 text-[#C87D46] font-semibold'
                        : 'text-[#E5E2DB] hover:bg-white/5 hover:text-[#C87D46]'
                    }`
                  }
                >
                  <span>{item.label}</span>
                  <i className="fa-solid fa-arrow-right text-[10px] text-white/30" aria-hidden="true" />
                </NavLink>
              )
            })}
          </nav>

          {/* Mobile contact strip */}
          <div className="mt-6 pt-4 border-t border-white/10 flex flex-col gap-2.5 text-xs text-[#A8A49C]">
            <div className="font-serif italic text-[#E5E2DB]">
              “Musik med nerve”
            </div>
            <div className="flex flex-col gap-1.5 text-sm">
              <a
                href="tel:+4522884536"
                className="flex items-center gap-2 text-[#F5F3EF] hover:text-[#C87D46] transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#C87D46] text-xs" aria-hidden="true" />
                <span className="whitespace-nowrap font-medium">+45 22 88 45 36</span>
              </a>
              <a
                href="mailto:jens@jarcon.dk"
                className="flex items-center gap-2 text-[#F5F3EF] hover:text-[#C87D46] transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-[#C87D46] text-xs" aria-hidden="true" />
                <span className="whitespace-nowrap font-medium">jens@jarcon.dk</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
